import React from 'react';
import {
  ToggleButton as MuiToggleButton,
  ToggleButtonProps as MuiToggleButtonProps,
} from '@mui/material';
import { ButtonSize } from '../Button';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export type ToggleButtonVariant = 'primary' | 'secondary';

export interface ToggleButtonProps
  extends Omit<MuiToggleButtonProps, 'size' | 'color' | keyof PounceStyleProps>, PounceStyleProps {
  /** The value to associate with the button */
  value: string | boolean;

  /** The variant of the toggle button, defaults to primary */
  variant?: ToggleButtonVariant;

  /** The size of the toggle button, defaults to large */
  size?: ButtonSize;

  /** Whether the button is selected */
  selected?: boolean;

  /** Callback fired when the button is clicked */
  onClick?: (event: React.MouseEvent<HTMLElement>, value: string | boolean) => void;

  /** Callback fired when the selection state changes */
  onChange?: (event: React.MouseEvent<HTMLElement>, value: string | boolean) => void;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiToggleButtonProps['sx'];
}

/**
 * ToggleButton component built on top of MUI ToggleButton.
 *
 * A button that can be toggled on or off. Typically used within a ToggleButtonGroup
 * for single or multiple selection scenarios.
 *
 */
export const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  function ToggleButton(
    {
      value,
      variant = 'primary',
      size = 'large',
      selected,
      onClick,
      onChange,
      children,
      sx,
      ...rest
    },
    ref,
  ) {
    const { pounceProps, otherProps } = extractPounceProps(rest);
    const pounceStyles = mapPouncePropsToSx(pounceProps);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (onClick) {
        onClick(event, value);
        if (event.defaultPrevented) {
          return;
        }
      }

      if (onChange) {
        onChange(event, value);
      }
    };

    // Combine pounce-generated styles with toggle button-specific styles and user sx
    const combinedSx: MuiToggleButtonProps['sx'] = {
      ...pounceStyles,
      ...(sx || {}),
    } as MuiToggleButtonProps['sx'];

    return (
      <MuiToggleButton
        ref={ref}
        value={value}
        selected={selected}
        onClick={handleClick}
        size={size}
        data-variant={variant}
        aria-selected={selected}
        sx={combinedSx}
        {...otherProps}
      >
        {children}
      </MuiToggleButton>
    );
  },
);
